/** @format */

import { produce } from "immer";
import React, { useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import { useLocale } from "../../i18n/hooks";
import { Card } from "../../resources/types";
import VisualEditor, {
  getDataWithoutCollaboration,
} from "../../VisualEditor/VisualEditor";
import { setIsLoading } from "../contentEditorSlice";

type Props = {
  card: Card;
  initCard: Card;
  updateCard: (value: Card) => void;
};

const AddContent: React.FC<Props> = ({ card, initCard, updateCard }) => {
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const dispatch = useAppDispatch();

  const updateCardContent = (value: string, field: "content" | "title") => {
    updateCard(
      produce(card, (draft) => {
        let initialObject = draft.Object.data[field];
        if (!initialObject || typeof initialObject === "string") {
          initialObject = {
            en: "",
            cy: "",
          };
        }

        draft.Object.data[field] = { ...initialObject, [locale]: value };
      }),
    );
  };

  return (
    <div>
      <Row>
        <Form.Group as={Col} className="mb-2">
          <Form.Label htmlFor="content-title">{_("Title")}</Form.Label>
          <Form.Control
            id="content-title"
            type="text"
            className="form-control form-control-lg"
            value={
              typeof card?.Object.data.title === "object"
                ? card?.Object.data.title[locale] ?? ""
                : card?.Object.data.title ?? ""
            }
            onChange={(e) => {
              updateCardContent(e.currentTarget.value, "title");
            }}
          />
          <Form.Control.Feedback type="invalid">
            {_("Please enter a title.")}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Form.Label htmlFor="ck-content">{_("Content")}</Form.Label>
      <div className="position-relative">
        <VisualEditor
          editorId={`content-${card.UUID}-${locale}`}
          data={
            typeof initCard?.Object.data.content === "object"
              ? initCard?.Object.data.content?.[locale] ?? ""
              : initCard?.Object.data.content ?? ""
          }
          // When ckEditor loads, save initial data so that any unsaved changes are saved
          onReady={async (editor) => {
            dispatch(setIsLoading(true));
            await getDataWithoutCollaboration(editor).then((data) => {
              updateCardContent(data, "content");
              dispatch(setIsLoading(false));
            });
          }}
          onChange={async (_, editor) => {
            clearTimeout(timer);
            dispatch(setIsLoading(true));
            const newTimer = setTimeout(async () => {
              await getDataWithoutCollaboration(editor).then((data) => {
                updateCardContent(data, "content");
                dispatch(setIsLoading(false));
              });
            }, 500);

            setTimer(newTimer);
          }}
        />
      </div>
    </div>
  );
};

export default AddContent;
